import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { contentService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft, Clock, Heart, Calendar, User, Share2, 
  Copy, Check, MessageCircle, Send, X, ChevronDown, ChevronUp,
  Bot, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

// Kategori renkleri
const CATEGORY_COLORS = {
  ev_arac: { bg: 'bg-blue-500', text: 'text-blue-500', light: 'bg-blue-500/10' },
  sarj_teknoloji: { bg: 'bg-orange-500', text: 'text-orange-500', light: 'bg-orange-500/10' },
  gunes_enerjisi: { bg: 'bg-yellow-500', text: 'text-yellow-500', light: 'bg-yellow-500/10' },
  enerji_depolama: { bg: 'bg-green-500', text: 'text-green-500', light: 'bg-green-500/10' },
  genel: { bg: 'bg-purple-500', text: 'text-purple-500', light: 'bg-purple-500/10' },
};

const CATEGORY_LABELS = {
  ev_arac: 'Elektrikli Araçlar',
  sarj_teknoloji: 'Şarj Teknolojileri',
  gunes_enerjisi: 'Güneş Enerjisi',
  enerji_depolama: 'Enerji Depolama',
  genel: 'Genel',
};

const DEFAULT_COVER = 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=1200&auto=format&fit=crop&q=80';

// Paylaşım Dropdown Componenti
const ShareDropdown = ({ title, url }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const dropdownRef = useRef(null);

  // Dışarı tıklanınca kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const encodedTitle = encodeURIComponent(title);
  const encodedUrl = encodeURIComponent(url);

  const shareOptions = [
    {
      name: 'WhatsApp',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      ),
      color: 'bg-green-500 hover:bg-green-600',
      href: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
    },
    {
      name: 'X (Twitter)',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      color: 'bg-black hover:bg-gray-800',
      href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
    },
    {
      name: 'LinkedIn',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
      color: 'bg-blue-600 hover:bg-blue-700',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
    {
      name: 'Facebook',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      color: 'bg-blue-500 hover:bg-blue-600',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
  ];

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Link kopyalandı!');
    } catch (err) {
      toast.error('Link kopyalanamadı');
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-muted hover:bg-primary/10 hover:text-primary transition-all"
      >
        <Share2 className="w-4 h-4" />
        <span className="font-medium">Paylaş</span>
      </button>
      
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="p-2">
            {shareOptions.map((option) => (
              <a
                key={option.name}
                href={option.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-white ${option.color} transition-colors mb-1`}
                onClick={() => setIsOpen(false)}
              >
                {option.icon}
                <span className="font-medium">{option.name}</span>
              </a>
            ))}
            
            <button
              onClick={handleCopyLink}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-muted hover:bg-accent transition-colors text-foreground"
            >
              {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
              <span className="font-medium">{copied ? 'Kopyalandı!' : 'Linki Kopyala'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// AI Sohbet Kutusu Componenti
const AIChatBox = ({ articleTitle, articleSummary }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const suggestedQuestions = [
    "Bu teknoloji Türkiye'de ne zaman yaygınlaşır?",
    "Avantajları ve dezavantajları neler?",
    "Daha fazla bilgi ver"
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || isLoading) return;

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await contentService.chat(
        text,
        articleTitle,
        articleSummary,
        messages
      );

      if (response.success) {
        setMessages(prev => [...prev, { role: 'assistant', content: response.response }]);
      } else {
        throw new Error('AI yanıt vermedi');
      }
    } catch (error) {
      toast.error('AI yanıt veremedi');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Üzgünüm, şu an yanıt veremiyorum. Lütfen daha sonra tekrar deneyin.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <div className="mt-12 border border-border rounded-2xl overflow-hidden bg-card">
      {/* Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-6 py-4 bg-gradient-to-r from-primary/10 to-purple-500/10 hover:from-primary/20 hover:to-purple-500/20 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <Bot className="w-5 h-5 text-primary" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-foreground">Bu Haber Hakkında AI'ya Sor</h3>
            <p className="text-sm text-muted-foreground">EV teknolojileri hakkında sorularınızı yanıtlıyorum</p>
          </div>
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>

      {/* Chat Area */}
      {isOpen && (
        <div className="border-t border-border">
          {/* Messages */}
          <div className="h-80 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <Sparkles className="w-12 h-12 mx-auto text-primary/50 mb-3" />
                <p className="text-muted-foreground mb-4">Bu makale hakkında bana soru sorabilirsiniz</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {suggestedQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(q)}
                      className="px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary text-sm rounded-full transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground rounded-br-md'
                      : 'bg-muted text-foreground rounded-bl-md'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-muted px-4 py-3 rounded-2xl rounded-bl-md">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="border-t border-border p-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Sorunuzu yazın..."
                className="flex-1 px-4 py-2.5 rounded-full border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="px-4 py-2.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// Ana Sayfa Componenti
const NewsDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticle();
  }, [id]);

  const loadArticle = async () => {
    try {
      setLoading(true);
      const data = await contentService.get(id);
      setArticle(data);
    } catch (error) {
      toast.error('Haber yüklenirken hata oluştu');
      navigate(user?.role === 'calisan' ? '/technician/news' : '/news');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!article) return;
    try {
      const response = await contentService.toggleLike(id);
      setArticle(prev => ({
        ...prev,
        likes_count: response.likes_count,
        liked_by: response.user_liked 
          ? [...(prev.liked_by || []), user.id]
          : (prev.liked_by || []).filter(uid => uid !== user.id)
      }));
    } catch (error) {
      toast.error('Beğeni işlemi başarısız');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="text-center py-16">
        <h2 className="text-xl font-semibold">Haber bulunamadı</h2>
        <Button onClick={() => navigate(user?.role === 'calisan' ? '/technician/news' : '/news')} className="mt-4">
          Haberlere Dön
        </Button>
      </div>
    );
  }

  const colors = CATEGORY_COLORS[article.category] || CATEGORY_COLORS.genel;
  const isLiked = article.liked_by?.includes(user?.id);
  const currentUrl = window.location.href;

  return (
    <div className="max-w-4xl mx-auto" data-testid="news-detail-page">
      {/* Back Button - Teknisyen için /technician/news, diğerleri için /news */}
      <Button 
        variant="ghost" 
        onClick={() => navigate(user?.role === 'calisan' ? '/technician/news' : '/news')}
        className="mb-6 -ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Haberlere Dön
      </Button>

      {/* Cover Image */}
      <div className="relative w-full h-[300px] md:h-[400px] rounded-2xl overflow-hidden mb-8">
        <img 
          src={article.cover_image || DEFAULT_COVER} 
          alt={article.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        {/* Category Badge */}
        <span className={`absolute bottom-6 left-6 px-4 py-1.5 rounded-full text-sm font-semibold ${colors.bg} text-white`}>
          {CATEGORY_LABELS[article.category]}
        </span>
      </div>

      {/* Article Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight">
          {article.title}
        </h1>
        
        <p className="text-lg text-muted-foreground mb-6">
          {article.summary}
        </p>

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground pb-6 border-b border-border">
          <span className="flex items-center gap-1.5">
            <User className="w-4 h-4" />
            {article.author_name}
          </span>
          <span className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4" />
            {formatDate(article.published_at || article.created_at)}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            {article.read_time} dk okuma
          </span>
          
          <div className="flex-1" />
          
          {/* Actions */}
          <button 
            onClick={handleLike}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full transition-all ${
              isLiked 
                ? 'bg-red-500/10 text-red-500' 
                : 'bg-muted hover:bg-red-500/10 hover:text-red-500'
            }`}
          >
            <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
            <span className="font-medium">{article.likes_count || 0}</span>
          </button>
          
          {/* Share Dropdown */}
          <ShareDropdown title={article.title} url={currentUrl} />
        </div>
      </div>

      {/* Article Body */}
      <article 
        className="prose prose-lg dark:prose-invert max-w-none mb-12"
        dangerouslySetInnerHTML={{ __html: article.body }}
      />

      {/* Footer */}
      <div className="flex items-center justify-between py-6 border-t border-border">
        <Button variant="outline" onClick={() => navigate(user?.role === 'calisan' ? '/technician/news' : '/news')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Diğer Haberler
        </Button>
        
        <div className="flex items-center gap-3">
          <button 
            onClick={handleLike}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition-all ${
              isLiked 
                ? 'bg-red-500 text-white' 
                : 'bg-muted hover:bg-red-500 hover:text-white'
            }`}
          >
            <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
            Beğen ({article.likes_count || 0})
          </button>
        </div>
      </div>

      {/* AI Chat Box */}
      <AIChatBox 
        articleTitle={article.title} 
        articleSummary={article.summary} 
      />
    </div>
  );
};

export default NewsDetailPage;
